import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { VAMethodologyService } from "../../../Component/Api/VerificationAgency/VaMethodology/VAMethodologyService";
import { ConfirmationDialog } from "../../../Component/Common/Components/ConfirmationDialog";
import { DataTable } from "../../../Component/Common/Components/DataTable/DataTable";
import { ColumnSortOption } from "../../../Component/Model/Dto/ColumnSortOption";
import { VaMethodology } from "../../../Component/Model/Dto/VerificationAgency/VaMethodology";
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { VAMethodologyTableMapper } from "./VAMethodologyTableMapper";

interface IProps {
    scorecardElementType: number,
}

export const VaMethodologyPage = (props: IProps) => {
    const [vaMethodologySearchResult, setVaMethodologySearchResult] = useState<VaMethodology.VaMethodologySearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);
    const [vaMethodologyId, setVaMethodologyId] = useState<number>(0);


    const vaMethodologySearchRequest: VaMethodology.VaMethodologySearchRequest = {
        columnSortOptions: columnSortOptions,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue as number,
        searchValue: searchValue,
        scorecardElementType: props.scorecardElementType
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const moveUp = async (id: number) => {
        try {
            await VAMethodologyService.MoveVAMethodology(id, 'up', props.scorecardElementType)
        } catch (error) {
            console.log(error)
        }
        fetchData();
    }

    const moveDown = async (id: number) => {
        try {
            await VAMethodologyService.MoveVAMethodology(id, 'down', props.scorecardElementType)
        } catch (error) {
            console.log(error)
        }
        fetchData();
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let response = await VAMethodologyService.Search(vaMethodologySearchRequest);
            setVaMethodologySearchResult(response.data.results);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setTotalItemsCount(response.data.totalItemsCount);
        } catch (error) {
            console.log(error)
        }
        finally {
            setIsLoading(false);
        }
    }

    const toggleMethodology = async (id: number, value: boolean) => {
        setIsLoading(true);
        try {
            await VAMethodologyService.Include(id, value);
            fetchData();
        } catch (error) {
            console.log(error)
        }
        finally {
            setIsLoading(false);
        }
    }

    const deleteMethodology = async (id: number) => {
        setShowDeleteConfirmationDialog(true);
        setVaMethodologyId(id)
    }

    const handleDelete = async () => {
        setIsLoading(true);
        try {
            await VAMethodologyService.Delete(vaMethodologyId);
            fetchData();
            toast.success("Methodology Deleted")
            setShowDeleteConfirmationDialog(false);
        } catch (error) {
            console.log(error)
        }
        finally {
            setIsLoading(false);
        }

    };

    const handleCloseDeleteConfirmation = () => {
        setShowDeleteConfirmationDialog(false);
        setVaMethodologyId(0);
    }

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, searchValue, pageNumber, columnSortOptions, props.scorecardElementType]);

    const vaMethodologyTableData = VAMethodologyTableMapper(
        vaMethodologySearchResult,
        filteredItemsCount,
        pageNumber,
        vaMethodologySearchRequest.pageSize,
        toggleMethodology,
        deleteMethodology,
        moveUp,
        moveDown
    );

    return (
        <>
            <DataTable
                table={vaMethodologyTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                canSearch={true}
                searchValueChanged={searchValueChanged}
                manualSorting={true}
                columnSortingChanged={columnSortingChanged}
            />
            <ConfirmationDialog
                showDialog={showDeleteConfirmationDialog}
                handleAccept={handleDelete}
                handleCancel={handleCloseDeleteConfirmation}
                heading={"Confirm Delete"}
                message={"Are you sure you want to delete this item?"}
            />
        </>
    )
}
